import { useRouter } from 'next/router';
import { signIn } from 'next-auth/react';
import React, { useEffect, useState } from 'react';

import { useMetrics } from '../../../hooks/useMetrics';
import { buildSignupSuccessCallback } from '../../../utils/adsConversionTrackingUtils';
import { AUTH_EVENTS, clearAuthPending, setAuthPending } from '../../../utils/authFunnel';
import { isUnSupportedBrowser } from '../../../utils/navigatorUtils';
import {
  ChevIco,
  ClassLinkIco,
  CleverIco,
  FeideIco,
  GoogleIco,
  MailIco,
} from './AuthIcons';
import {
  AuthHead,
  AuthTitle,
  Field,
  FieldErr,
  FieldGroup,
  FieldHint,
  FieldLabel,
  Legal,
  OAuthTile,
  OAuthTiles,
  OrDivider,
  OtherProvidersLink,
  PrimaryBtn,
  SupportRef,
  SwitchLine,
} from './authStyles';
import MagicLinkCodeConfirm from './MagicLinkCodeConfirm';
import { AuthStrings } from './useAuthStrings';

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export type AuthMode = 'login' | 'signup';

type Props = {
  t: AuthStrings;
  mode: AuthMode;
  setMode: (m: AuthMode) => void;
  callbackUrl: string;
  isNorway?: boolean;
};

/**
 * Inline email-first auth form: the magic-link email field is shown immediately,
 * with SSO providers below. All providers use the new (non-Azure) NextAuth
 * provider ids. The magic-link send/verify is owned by the backend (CURI-334);
 * the UI calls the Email provider and a code-verify endpoint and degrades
 * gracefully until that backend is live.
 */
const AuthForm: React.FC<Props> = ({ t, mode, setMode, callbackUrl, isNorway }) => {
  const [step, setStep] = useState<'form' | 'code'>('form');
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  // Support reference (backend correlation id) shown when a code can't be sent.
  const [errRef, setErrRef] = useState<string | undefined>();
  const [showMore, setShowMore] = useState(false);
  const [sending, setSending] = useState(false);
  // In-app browsers (Facebook / TikTok / LinkedIn) block or break third-party
  // OAuth pop-ups, so we hide the SSO providers and offer only the magic link
  // there. Detected after mount (navigator is client-only) to avoid a hydration
  // mismatch — defaults to false so SSR renders the full set.
  const [oauthUnsupported, setOauthUnsupported] = useState(false);
  // Failed-code message seeded onto the code screen after an in-app-browser
  // navigation bounce (see the codeError effect below).
  const [codeError, setCodeError] = useState('');
  const metrics = useMetrics(true);
  const router = useRouter();

  useEffect(() => {
    setOauthUnsupported(isUnSupportedBrowser());
  }, []);

  const isSignup = mode === 'signup';

  // On signup, route the post-auth destination through /signup/success so
  // ad-conversion (CompleteRegistration) + analytics fire — matching legacy
  // useSignUp. Login goes straight to the destination. Pure (no side effects).
  const effectiveCallbackUrl = (dest: string) =>
    isSignup ? buildSignupSuccessCallback(dest) : dest;

  // Log the Signup funnel event once per signup initiation (provider click or
  // code request) — not on every magic-link verify retry, which would inflate it.
  const logSignupInitiated = () => {
    if (isSignup) metrics.logEvent('Signup', undefined, true);
  };

  useEffect(() => {
    setStep('form');
    setErr('');
    // Toggling login↔signup means the user reconsidered, so any in-flight pending
    // marker (from a started OAuth attempt or a sent magic-link code) is now stale
    // — drop it so the next concrete action re-sets it with the current mode and
    // Auth.Succeeded isn't attributed to the wrong mode/method.
    clearAuthPending();
  }, [mode]);
  useEffect(() => {
    setErr('');
    setErrRef(undefined);
  }, [email]);
  // The in-app-browser verify flow (navigation to /api/auth/magic-link/verify)
  // bounces back here with ?codeError=1&email=… on a bad/expired code. The emailed
  // code is still valid, so drop the user straight back on the code screen with the
  // email prefilled to retype it — no new code requested. Email comes via the URL
  // (in-app browsers drop the cookies we'd otherwise use) and is stripped
  // immediately below so it doesn't linger in history. Falls back to an email-step
  // error if it's missing. Declared last so it wins over the on-mount clears above.
  // Runs once.
  useEffect(() => {
    if (!router.query.codeError) return;
    const emailParam =
      typeof router.query.email === 'string' ? router.query.email : undefined;
    if (emailParam) {
      setEmail(emailParam);
      setStep('code');
      setCodeError(t.errCodeInvalid);
    } else {
      setErr(t.errCodeInvalid);
    }
    const { codeError: _codeError, email: _email, ...rest } = router.query;
    router.replace({ pathname: router.pathname, query: rest }, undefined, {
      shallow: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onProvider = (provider: 'google' | 'clever' | 'classlink' | 'feide') => {
    // Explicitly use the exchange flow (not self-join) — overwrites any stale
    // self-join cookie from a prior visit to an SSO entry page. Same-origin
    // top-level GET → SameSite=Lax is sent. The backend infers signup vs login,
    // so there's no intent to carry through.
    document.cookie = 'curipod_auth_flow=exchange; path=/; max-age=600; samesite=lax';
    metrics.logEvent(AUTH_EVENTS.methodSelected, { mode, method: 'oauth', provider });
    // Remember the attempt so Auth.Succeeded can fire after the OAuth redirect.
    setAuthPending({ mode, method: 'oauth', provider });
    logSignupInitiated();
    signIn(provider, { callbackUrl: effectiveCallbackUrl(callbackUrl) });
  };

  // Request a 6-digit magic-link code (proxied to the backend's public
  // /auth/magic-link/request, CURI-334). Returns the outcome — plus the backend
  // correlation id on failure so the UI can show a support reference.
  const requestCode = async (): Promise<{
    status: 'ok' | 'rate-limited' | 'error';
    correlationId?: string;
  }> => {
    try {
      const res = await fetch('/api/auth/magic-link/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.status === 429) return { status: 'rate-limited' };
      if (res.ok) return { status: 'ok' };
      let correlationId: string | undefined;
      try {
        correlationId = (await res.json())?.correlationId;
      } catch {
        // body not JSON — no reference to show
      }
      return { status: 'error', correlationId };
    } catch {
      return { status: 'error' };
    }
  };

  const sendLink = async () => {
    if (sending) return; // in-flight guard against rapid clicks / repeated Enter
    if (!email.trim()) {
      setErr(t.enterEmail);
      setErrRef(undefined);
      return;
    }
    if (!isEmail(email)) {
      setErr(t.invalidEmail);
      setErrRef(undefined);
      return;
    }
    // Funnel: the user committed to the magic-link method.
    metrics.logEvent(AUTH_EVENTS.methodSelected, { mode, method: 'magicLink' });
    setSending(true);
    try {
      const { status, correlationId } = await requestCode();
      if (status === 'rate-limited') {
        metrics.logEvent(AUTH_EVENTS.sendFailed, { mode, reason: 'rate-limited' });
        setErr(t.errTooManyRequests);
        setErrRef(undefined);
        return;
      }
      if (status === 'error') {
        metrics.logEvent(AUTH_EVENTS.sendFailed, { mode, reason: 'error' });
        setErr(t.errSendCodeFailed);
        setErrRef(correlationId);
        return;
      }
      // Count the signup once, when the code was actually sent.
      logSignupInitiated();
      metrics.logEvent(AUTH_EVENTS.codeSent, { mode });
      setAuthPending({ mode, method: 'magicLink' });
      setStep('code');
    } finally {
      setSending(false);
    }
  };

  // Verify the code via the `magic-link` CredentialsProvider, which calls the
  // backend and establishes the NextAuth session on success.
  const verifyCode = async (code: string) => {
    const relativeCallback = effectiveCallbackUrl(callbackUrl);

    // In-app browsers (Facebook / Instagram / TikTok / LinkedIn) drop cookies set
    // on background XHR responses, so NextAuth's fetch-based `signIn` never
    // establishes a session there — the correct code looks like it "doesn't work".
    // Submit a real form instead: `/api/auth/magic-link/verify` verifies the code
    // and sets the session cookie on a top-level 302 navigation, which the WebView
    // persists. A bad code redirects back to the auth screen with ?codeError=1.
    if (isUnSupportedBrowser()) {
      // Re-arm the pending marker on every navigation attempt. A failed attempt
      // does a full reload where AuthExperience calls clearAuthPending(), so
      // without this a successful retry would land in the app with no marker and
      // Auth.Succeeded would never fire. Setting it right before navigating means
      // it survives to the post-success app load (nothing clears it in between).
      setAuthPending({ mode, method: 'magicLink' });
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/auth/magic-link/verify';
      const fields: Record<string, string> = {
        email: email.trim(),
        code,
        callbackUrl: relativeCallback,
        failureUrl: isSignup ? '/signup' : '/login',
        // Raw (unwrapped) destination so a failed attempt can bounce back to the
        // auth screen with ?redirect= preserved — the wrapped callbackUrl above is
        // for the success 302 only.
        redirect: callbackUrl,
      };
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
      // Navigation is underway; never resolve so the "signing you in" overlay
      // stays up until the page unloads.
      await new Promise<never>(() => undefined);
      return;
    }

    // Normal browsers: inline XHR flow. NextAuth runs `new URL(data.url)` for
    // credentials + redirect:false, and our redirect callback passes the URL
    // through unchanged — so the callbackUrl must be absolute, or a relative "/"
    // throws "Failed to construct 'URL'".
    const absoluteCallback = new URL(relativeCallback, window.location.origin).href;
    const res = await signIn('magic-link', {
      email: email.trim(),
      code,
      redirect: false,
      callbackUrl: absoluteCallback,
    });
    if (!res || res.error) {
      throw new Error(t.errCodeInvalid);
    }
    // Signup wraps the destination through /signup/success, where the ad
    // CompleteRegistration + Signup.Success events fire on load. A soft router.push
    // races the signup page's own isLoggedIn effect: signIn's session refetch flips
    // useSession to authenticated, that effect pushes the RAW destination, and if it
    // wins /signup/success is skipped — dropping conversion tracking. A full-page
    // navigation tears that effect down so /signup/success is reached deterministically
    // (and it needs a real load to fire its tracking anyway).
    if (isSignup) {
      window.location.assign(relativeCallback);
      // Navigation is underway; never resolve so the "signing you in" overlay stays
      // up until the page unloads (matches the in-app-browser branch above).
      await new Promise<never>(() => undefined);
      return;
    }
    // Login has no success wrapper (relativeCallback === destination) and no competing
    // effect, so signIn's already-refetched session makes a client-side transition to
    // relativeCallback safe — no full page reload needed.
    router.push(relativeCallback);
  };

  if (step === 'code') {
    return (
      <MagicLinkCodeConfirm
        t={t}
        email={email}
        mode={mode}
        onVerify={verifyCode}
        onResend={requestCode}
        onTryAnother={() => setStep('form')}
        initialError={codeError}
      />
    );
  }

  return (
    <>
      <AuthHead>
        <AuthTitle>{isSignup ? t.signupTitle : t.loginTitle}</AuthTitle>
      </AuthHead>

      <FieldGroup>
        <FieldLabel htmlFor="email">{t.email}</FieldLabel>
        <Field
          id="email"
          type="email"
          $err={!!err}
          placeholder={t.emailPh}
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') sendLink();
          }}
          autoComplete="email"
        />
        {err ? (
          <FieldErr>
            {err}
            {errRef ? (
              <SupportRef>
                {t.errSupportRef}: <code>{errRef}</code>
              </SupportRef>
            ) : null}
          </FieldErr>
        ) : email.trim() ? (
          <FieldHint>
            <MailIco />
            {t.secureHint}
          </FieldHint>
        ) : null}
      </FieldGroup>

      <PrimaryBtn onClick={sendLink} disabled={sending}>
        {t.continue}
      </PrimaryBtn>

      {/* In-app browsers (Facebook / TikTok / LinkedIn) block third-party OAuth
          pop-ups, so the SSO providers are hidden there — magic link only. */}
      {!oauthUnsupported && (
        <>
          <OrDivider>{t.orContinue}</OrDivider>

          <OAuthTiles $cols={isNorway ? 2 : 3}>
            <OAuthTile onClick={() => onProvider('google')}>
              <GoogleIco />
              <span>Google</span>
            </OAuthTile>
            {/* Feide is Norway's national SSO — surface it next to Google there
                instead of tucked under "other options"; Clever/ClassLink move
                into "other options" to make room. */}
            {isNorway ? (
              <OAuthTile onClick={() => onProvider('feide')}>
                <FeideIco />
                <span>Feide</span>
              </OAuthTile>
            ) : (
              <>
                <OAuthTile onClick={() => onProvider('clever')}>
                  <CleverIco />
                  <span>Clever</span>
                </OAuthTile>
                <OAuthTile onClick={() => onProvider('classlink')}>
                  <ClassLinkIco />
                  <span>ClassLink</span>
                </OAuthTile>
              </>
            )}
          </OAuthTiles>
          {showMore && (
            <OAuthTiles $cols={isNorway ? 2 : 1} className="more-tiles">
              {isNorway ? (
                <>
                  <OAuthTile onClick={() => onProvider('clever')}>
                    <CleverIco />
                    <span>Clever</span>
                  </OAuthTile>
                  <OAuthTile onClick={() => onProvider('classlink')}>
                    <ClassLinkIco />
                    <span>ClassLink</span>
                  </OAuthTile>
                </>
              ) : (
                <OAuthTile onClick={() => onProvider('feide')}>
                  <FeideIco />
                  <span>Feide</span>
                </OAuthTile>
              )}
            </OAuthTiles>
          )}
          <OtherProvidersLink onClick={() => setShowMore(m => !m)}>
            {showMore ? t.hideOther : t.otherProviders}
            <ChevIco className={showMore ? 'up' : ''} />
          </OtherProvidersLink>
        </>
      )}

      <SwitchLine>
        {isSignup ? t.haveAccount : t.newHere}{' '}
        <button onClick={() => setMode(isSignup ? 'login' : 'signup')}>
          {isSignup ? t.login : t.signup}
        </button>
      </SwitchLine>

      {isSignup && (
        <Legal>
          {t.legalPre}{' '}
          <a href="/terms-of-use" target="_blank" rel="noreferrer">
            {t.terms}
          </a>{' '}
          {t.and}{' '}
          <a href="/privacy-policy" target="_blank" rel="noreferrer">
            {t.privacy}
          </a>
          {t.legalPost}
        </Legal>
      )}
    </>
  );
};

export default AuthForm;
